import { Folder } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { getBusinessContacts } from 'lib/api/contacts';
import {
  getPortfoliosTemplates,
  getPortoflioValidations,
} from 'lib/api/integrations/digitalSignature';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { PortfolioTemplate, PortfolioValidation } from 'lib/types/models';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import CustomForm from './Components/CustomForm';
import TemplateForm from './Components/TemplateForm';

export type CustomHandle = {
  submit: () => Promise<void>;
};

interface CreatePortfolioProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  businessIdentifier: string | undefined;
  fetchPortfolios: (params: {
    page: number;
    pageSize: number;
  }) => Promise<void>;
}

const CreatePortfolio = ({
  open,
  setOpen,
  businessIdentifier,
  fetchPortfolios,
}: CreatePortfolioProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const ownIdentifier = user?.businessIdentifier;
  const [type, setType] = useState<'custom' | 'template'>('custom');
  const [templates, setTemplates] = useState<PortfolioTemplate[]>([]);
  const [validations, setValidations] = useState<PortfolioValidation[]>([]);

  const customFormRef = useRef<CustomHandle | null>(null);
  const templateFormRef = useRef<CustomHandle | null>(null);

  const query = new URLSearchParams({
    isLegalRepresentative: 'true',
  }).toString();

  const { isPending: isPendingOwnUsers } = getBusinessContacts(
    dispatch,
    ownIdentifier,
    query,
  );
  const { isPending: isPendingBusinessUsers } = getBusinessContacts(
    dispatch,
    businessIdentifier,
    query,
  );

  const isPending = isPendingOwnUsers || isPendingBusinessUsers;

  const fetchPortoflioValidations = async () => {
    try {
      const validations = await getPortoflioValidations(dispatch);
      setValidations(validations);
    } catch (error) {
      console.error('Error fetching validations:', error);
      showAlert(dispatch, 'error', 'Error fetching validations');
    }
  };

  const fetchTemplates = async () => {
    try {
      const templates = await getPortfoliosTemplates(dispatch);
      setTemplates(templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      if (type === 'custom') {
        await customFormRef.current?.submit();
      } else {
        await templateFormRef.current?.submit();
      }
      await fetchPortfolios({ page: 1, pageSize: 10 });
      setOpen(false);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al crear el portafolio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchTemplates();
    void fetchPortoflioValidations();
  }, []);

  if (isPending) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Title
          title="Crear portafolio"
          icon={<Folder color="primary" />}
          color="primary"
        />
        <FormControl margin="dense" size="small" style={{ width: '200px' }}>
          <InputLabel id="type-label">Tipo de portafolio</InputLabel>
          <Select
            labelId="type-label"
            id="type-select"
            size="small"
            value={type}
            label="Tipo de portafolio"
            onChange={(e) => setType(e.target.value as 'template' | 'custom')}
            MenuProps={{
              MenuListProps: { dense: true },
            }}
          >
            {['custom', 'template'].map((type: string, index: number) => (
              <MenuItem key={index} value={type} dense>
                {type === 'custom' ? 'Personalizado' : 'Plantilla'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogTitle>
      <DialogContent>
        {type === 'custom' && (
          <CustomForm
            ref={customFormRef}
            businessIdentifier={businessIdentifier}
            validations={validations}
          />
        )}
        {type === 'template' && (
          <TemplateForm
            ref={templateFormRef}
            templates={templates}
            businessIdentifier={businessIdentifier}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="primary" onClick={handleCreate} loading={loading}>
          Crear portafolio
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePortfolio;
